/**
 * SSE-based chat hook for the Anthropic Managed Agent.
 *
 * Talks to `/core/agent/managed-chat` which proxies messages to the managed
 * agent via the Anthropic Sessions API. The backend intercepts backend tools
 * (e.g. `generate_image`) transparently — the hook only sees `tool_use` /
 * `tool_result` events for those, identical to Anthropic-hosted tools.
 *
 * Returns a `UIMessage[]` compatible with the existing `ChatMessageList`
 * component so the same rendering path works for both Marcus and the managed
 * agent. Tool calls are rendered as proper `dynamic-tool` parts (same as
 * Marcus), and thinking blocks are surfaced as `thinking` parts.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { getAccessToken, resolveApiUrl } from "@/lib/jwt-auth-client";
import { buildAdsContextSnapshot } from "@/features/ads/lib/adsContextSnapshot";
import type { ChatAttachedImage } from "../lib/chatAttachments";
import {
  createNormalizedChatMessageAccumulator,
  createNormalizedChatStreamParser,
  type ManagedToolResultEvent,
  type NormalizedChatStreamEvent,
} from "../lib/normalizedChatStream";
import { useDepartmentStore } from "@/features/department";
import { useAdsWorkspaceStore } from "@/features/ads";
import type { CompanionPromptDisplay } from "@/shared/lib/events";

export type { ManagedToolResultEvent } from "../lib/normalizedChatStream";

export interface ScoutSeedInput {
  workspaceId: string;
  productId?: string;
  productInput: Record<string, unknown>;
}

// ── Hook types ────────────────────────────────────────────────────────────

export type ManagedChatStatus = "ready" | "submitted" | "streaming";

export interface UseManagedAgentChatOptions {
  /** Which managed agent to talk to (passed as `agentId` to the backend). */
  agentId?: string;
  /** Optional title for newly-created managed sessions. */
  title?: string;
  /**
   * Phase 8-9 — when ``runtimeTarget === 'unified'``, the POST is routed
   * to ``/core/agent/unified-chat`` (which expects ``agentName`` in the
   * body) instead of ``/core/agent/managed-chat`` (Anthropic SDK runtime).
   * The SSE response shape is NormalizedEvent-compatible on both
   * endpoints (Phase 3 contract tests verified this).
   */
  runtimeTarget?: "unified" | "anthropic_sdk";
  /** Required when ``runtimeTarget === 'unified'`` — body field for the unified loop. */
  agentName?: string;
  /** Optional workspace scope for newly-created unified sessions and dispatch tools. */
  workspaceId?: string | null;
  /** Optional metadata passed to unified-runtime pre-step hooks. */
  context?: Record<string, unknown> | null;
  /** Restore a previously cached session ID (e.g. when switching back). */
  initialSessionId?: string | null;
  /** Restore previously cached messages (e.g. when switching back). */
  initialMessages?: UIMessage[];
  /** Called when a backend tool result contains a generated image URL. */
  onImageGenerated?: (imageUrl: string) => void;
  /** Called when a tool result contains a generated video URL (e.g. Kling). */
  onVideoGenerated?: (videoUrl: string, thumbnailUrl?: string) => void;
  /** Called for every server-side tool result. Used by Scout to hydrate UI state. */
  onToolResult?: (event: ManagedToolResultEvent) => void;
  /** Called the moment the agent emits a `tool_use` (before the server-side
   *  handler runs). SubAgentChatView wires this into the companion tool
   *  dispatcher so UI tools (cursor walkthroughs, wizard control) fire
   *  optimistically without waiting for the tool_result envelope. */
  onToolUse?: (event: { id: string; name: string; input: unknown }) => void;
  /** Called for Scout research phase progress emitted on the session event bus. */
  onScoutPhase?: (event: Extract<NormalizedChatStreamEvent, { type: "scout_phase" }>) => void;
  /**
   * Called for `generate_image` streaming progress. Thought summaries arrive
   * as `kind: 'thought'` with `text`; intermediate image variants arrive as
   * `kind: 'draft'` with a renderable `imageUrl`; the canonical result is
   * marked `kind: 'final'`. Events are bus-only — observers (e.g.
   * SubAgentChatView) see them via the GET stream route.
   */
  onImageProgress?: (
    event: Extract<NormalizedChatStreamEvent, { type: "image_progress" }>,
  ) => void;
  /** Server-side Scout state seed sent only when creating a fresh Scout session. */
  scoutSeed?: ScoutSeedInput | null;
}

export interface UseManagedAgentChatResult {
  messages: UIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>;
  status: ManagedChatStatus;
  error: Error | undefined;
  sessionId: string | null;
  queuedMessageCount: number;
  sendMessage: (
    text: string,
    images?: ChatAttachedImage[],
    display?: CompanionPromptDisplay,
  ) => void;
  stopMessage: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────

let _msgCounter = 0;
function nextMsgId(): string {
  return `managed-${++_msgCounter}-${Date.now()}`;
}

/** Strip `data:<mime>;base64,` prefix to get raw base64 data. */
function stripDataUrlPrefix(dataUrl: string): string {
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

const VIDEO_EDITOR_AGENT_NAME = "marcus_video_editor";

interface QueuedManagedTurn {
  text: string;
  images?: ChatAttachedImage[];
  display?: CompanionPromptDisplay;
}

interface ChatErrorBody {
  error?: string;
  code?: string;
  message?: string;
}

function pickVideoEditorContext(
  context: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const projectId = context?.videoEditorProjectId;
  return typeof projectId === "string" && projectId.trim()
    ? { videoEditorProjectId: projectId.trim() }
    : null;
}

async function readChatErrorBody(res: Response): Promise<ChatErrorBody> {
  try {
    const data = (await res.json()) as ChatErrorBody;
    return data && typeof data === "object" ? data : {};
  } catch {
    return {};
  }
}

function isSessionTooLongError(data: ChatErrorBody): boolean {
  return data.code === "session_too_long" || data.error === "session_too_long";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseHistoryMessages(payload: unknown): UIMessage[] {
  if (!isRecord(payload) || !Array.isArray(payload.messages)) return [];
  const messages: UIMessage[] = [];
  for (const raw of payload.messages) {
    if (!isRecord(raw)) continue;
    const id = typeof raw.id === "string" ? raw.id : null;
    const role = raw.role === "user" || raw.role === "assistant" ? raw.role : null;
    if (!id || !role || !Array.isArray(raw.parts)) continue;
    messages.push({
      id,
      role,
      parts: raw.parts as UIMessage["parts"],
    });
  }
  return messages;
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useManagedAgentChat(
  options?: UseManagedAgentChatOptions,
): UseManagedAgentChatResult {
  const agentId = options?.agentId;
  const runtimeTarget = options?.runtimeTarget ?? "anthropic_sdk";
  const agentName = options?.agentName;
  const workspaceId = options?.workspaceId ?? null;
  const context = options?.context ?? null;
  const initialSessionId = options?.initialSessionId ?? null;
  const initialMessages = options?.initialMessages;

  const [messages, setMessages] = useState<UIMessage[]>(initialMessages ?? []);
  const [status, setStatus] = useState<ManagedChatStatus>("ready");
  const [error, setError] = useState<Error | undefined>();
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId);
  const [queuedMessageCount, setQueuedMessageCount] = useState(0);
  const sessionIdRef = useRef<string | null>(initialSessionId);
  const agentIdRef = useRef(agentId);
  const agentNameRef = useRef(agentName);
  const titleRef = useRef(options?.title);
  const onImageGeneratedRef = useRef(options?.onImageGenerated);
  const onVideoGeneratedRef = useRef(options?.onVideoGenerated);
  const onToolResultRef = useRef(options?.onToolResult);
  const onToolUseRef = useRef(options?.onToolUse);
  const onScoutPhaseRef = useRef(options?.onScoutPhase);
  const onImageProgressRef = useRef(options?.onImageProgress);
  const scoutSeedRef = useRef(options?.scoutSeed ?? null);
  const workspaceIdRef = useRef(workspaceId);
  const contextRef = useRef(context);
  // Guard against double-sends while streaming
  const busyRef = useRef(false);
  const queuedTurnsRef = useRef<QueuedManagedTurn[]>([]);
  const activeControllerRef = useRef<AbortController | null>(null);
  const stopRequestedRef = useRef(false);
  // Tracks messages.length so the observer effect can decide whether to
  // request server-side replay without having `messages` in its dep array
  // (which would reconnect SSE on every token).
  const messagesLengthAtSubscribeRef = useRef(messages.length);
  messagesLengthAtSubscribeRef.current = messages.length;

  // Keep refs in sync
  useEffect(() => {
    onImageGeneratedRef.current = options?.onImageGenerated;
  }, [options?.onImageGenerated]);
  useEffect(() => {
    onVideoGeneratedRef.current = options?.onVideoGenerated;
  }, [options?.onVideoGenerated]);
  useEffect(() => {
    onToolResultRef.current = options?.onToolResult;
  }, [options?.onToolResult]);
  useEffect(() => {
    onToolUseRef.current = options?.onToolUse;
  }, [options?.onToolUse]);
  useEffect(() => {
    onScoutPhaseRef.current = options?.onScoutPhase;
  }, [options?.onScoutPhase]);
  useEffect(() => {
    onImageProgressRef.current = options?.onImageProgress;
  }, [options?.onImageProgress]);
  useEffect(() => {
    workspaceIdRef.current = options?.workspaceId ?? null;
  }, [options?.workspaceId]);
  useEffect(() => {
    contextRef.current = options?.context ?? null;
  }, [options?.context]);
  useEffect(() => {
    scoutSeedRef.current = options?.scoutSeed ?? null;
  }, [options?.scoutSeed]);
  useEffect(() => {
    titleRef.current = options?.title;
  }, [options?.title]);
  // Keep agentId ref in sync
  useEffect(() => {
    agentIdRef.current = agentId;
  }, [agentId]);
  useEffect(() => {
    agentNameRef.current = agentName;
  }, [agentName]);

  // When initialSessionId / initialMessages change (agent switch), reset state
  useEffect(() => {
    stopRequestedRef.current = true;
    activeControllerRef.current?.abort();
    sessionIdRef.current = initialSessionId;
    setSessionId(initialSessionId);
    setMessages(initialMessages ?? []);
    setError(undefined);
    setStatus("ready");
    busyRef.current = false;
    queuedTurnsRef.current = [];
    setQueuedMessageCount(0);
  }, [initialSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unified-runtime sessions persist transcript events in Django. Hydrate the
  // visible chat from that transcript when Marcus is resumed after refresh or
  // when the user switches between workspace sessions.
  useEffect(() => {
    if (runtimeTarget !== "unified") return;
    const sid = sessionId;
    if (!sid) return;

    let cancelled = false;
    const controller = new AbortController();

    void (async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(
          resolveApiUrl(`/core/agent/runtime-sessions/${sid}/history`),
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          },
        );
        if (!res.ok) return;
        const hydrated = parseHistoryMessages(await res.json());
        if (cancelled || hydrated.length === 0) return;
        setMessages((current) => {
          if (cancelled || busyRef.current || sessionIdRef.current !== sid) return current;
          return current.length === 0 ? hydrated : current;
        });
      } catch {
        // Non-fatal — the next explicit turn still resumes model context
        // server-side through loadPriorMessages.
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [runtimeTarget, sessionId]);

  // ── Live observer stream ──────────────────────────────────────────────
  // When a sessionId is active, open a GET SSE to
  // `/core/agent/managed-chat/stream/:sessionId` and render every normalized
  // event the backend publishes to the session event bus. This is what lets
  // the sub-agent chat show Banan/Vid activity from headless dispatches
  // Marcus kicks off — `runHeadlessSession` publishes to the bus; the
  // interactive POST path does not, so there's no double-render when the
  // user chats in the same view.
  useEffect(() => {
    if (runtimeTarget !== "anthropic_sdk") return;
    const sid = sessionId;
    if (!sid) return;
    let aborted = false;
    let controller: AbortController | null = null;
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    // Flip to true on `session_terminated`. Once terminated, we stop
    // reconnecting — the session is dead on the server side and any further
    // subscribe attempt would just get re-closed.
    let terminated = false;

    const streamAccumulator = createNormalizedChatMessageAccumulator({
      createMessageId: nextMsgId,
      resetOnDone: true,
      commitMessage: (message, isUpdate) => {
        setMessages((prev) =>
          isUpdate
            ? prev.map((m) => (m.id === message.id ? message : m))
            : [...prev, message],
        );
      },
      callbacks: {
        onImageGenerated: (url) => onImageGeneratedRef.current?.(url),
        onVideoGenerated: (url, thumbnailUrl) =>
          onVideoGeneratedRef.current?.(url, thumbnailUrl),
        onToolResult: (event) => onToolResultRef.current?.(event),
        onToolUse: (event) => onToolUseRef.current?.(event),
        onScoutPhase: (event) => onScoutPhaseRef.current?.(event),
        onImageProgress: (event) => onImageProgressRef.current?.(event),
      },
    });

    const handleEvent = (event: NormalizedChatStreamEvent) => {
      const result = streamAccumulator.handleEvent(event);
      if (result.sessionTerminated) {
        terminated = true;
      }
    };

    /**
     * Fetch the authoritative session history from Anthropic's events.list
     * and overlay it onto local messages. Called before every reconnect and
     * on tab-visible so we catch up any events emitted while the observer
     * stream was disconnected (backend closes the stream on every `done`,
     * and proxies/browsers drop idle connections). Skips while a POST turn
     * is actively streaming locally — that path owns the in-progress
     * assistant bubble and must not be clobbered.
     */
    const syncHistory = async (): Promise<void> => {
      if (aborted || terminated || busyRef.current) return;
      try {
        const token = await getAccessToken();
        const res = await fetch(
          resolveApiUrl(`/core/agent/managed-chat/history/${sid}`),
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!res.ok) return;
        const data = await res.json();
        const msgs = data?.messages ?? data;
        if (!Array.isArray(msgs) || msgs.length === 0) return;
        if (aborted || busyRef.current) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deserialized = msgs.map((m: any) => ({
          id: m.id,
          role: m.role,
          parts: m.parts,
        })) as UIMessage[];
        setMessages((current) => {
          // Local has more messages than the server snapshot → a new turn
          // started after we fetched history. Don't clobber it; the next
          // reconnect cycle will overlay a fresher snapshot.
          if (current.length > deserialized.length) return current;
          return deserialized;
        });
      } catch {
        // non-fatal — next reconnect will retry
      }
    };

    /**
     * Open the observer SSE, read until it closes, then schedule a reconnect
     * with exponential backoff. The backend closes this stream on every
     * `done` event (end of a turn), so reconnection is the normal path —
     * not an error case. This is what lets the chat show:
     *   - Follow-up turns / headless dispatches on the same session.
     *   - Trailing text / video-ready messages emitted after a long tool
     *     call (Kling poll) without requiring a page reload.
     *   - Recovery from network-dropped connections (proxy timeouts, tab
     *     backgrounding, wifi hiccups).
     */
    const connect = async (): Promise<void> => {
      if (aborted || terminated) return;
      controller = new AbortController();
      const isInitialConnect = reconnectAttempts === 0;
      // Only ask the server to replay its ring buffer on the very first
      // connect AND only when we have nothing locally. Every reconnect uses
      // replay=false and relies on `syncHistory()` (above) for catch-up,
      // which prevents replay from duplicating turns already in state.
      const shouldReplay =
        messagesLengthAtSubscribeRef.current === 0 && isInitialConnect;
      if (!shouldReplay) {
        await syncHistory();
        if (aborted || terminated) return;
        // History overwrites messages with the authoritative snapshot, so
        // any partial turn the observer had accumulated locally is stale.
        streamAccumulator.reset();
      }
      const streamUrl = resolveApiUrl(
        `/core/agent/managed-chat/stream/${sid}${shouldReplay ? "" : "?replay=false"}`,
      );
      try {
        const token = await getAccessToken();
        const res = await fetch(streamUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const parser = createNormalizedChatStreamParser();

        while (true) {
          if (aborted) break;
          const { done, value } = await reader.read();
          if (done) break;
          for (const event of parser.pushChunk(decoder.decode(value, { stream: true }))) {
            handleEvent(event);
          }
        }
      } catch {
        // non-fatal — handled via reconnect below
      }
      if (aborted || terminated) return;
      reconnectAttempts += 1;
      // Exponential backoff capped at 5s: 300, 600, 1.2s, 2.4s, 4.8s, 5s…
      const delay = Math.min(
        5_000,
        300 * 2 ** Math.min(reconnectAttempts - 1, 4),
      );
      reconnectTimeoutId = setTimeout(() => {
        reconnectTimeoutId = null;
        void connect();
      }, delay);
    };

    // Browsers aggressively throttle (and sometimes silently drop) idle SSE
    // in backgrounded tabs. On foreground restore, accelerate any pending
    // reconnect and run a history sync so final tool results / trailing
    // agent text appear immediately instead of waiting for the backoff.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (aborted || terminated) return;
      void syncHistory();
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
        void connect();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    void connect();

    return () => {
      aborted = true;
      controller?.abort();
      if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [runtimeTarget, sessionId]);

  const sendMessage = useCallback(
    async (
      text: string,
      images?: ChatAttachedImage[],
      display?: CompanionPromptDisplay,
    ) => {
      if (!text.trim()) return;
      if (busyRef.current) {
        queuedTurnsRef.current.push({ text, images, display });
        setQueuedMessageCount(queuedTurnsRef.current.length);
        return;
      }
      busyRef.current = true;
      stopRequestedRef.current = false;
      setError(undefined);
      setStatus("submitted");
      const currentScoutSeed = scoutSeedRef.current;
      const currentTitle = titleRef.current;
      const controller = new AbortController();
      activeControllerRef.current = controller;

      // ── Append user message to state ─────────────────────────────────
      const userMsgId = nextMsgId();
      const userParts = display
        ? ([
            { type: "data-companion-command", data: display },
          ] as unknown as UIMessage["parts"])
        : ([{ type: "text" as const, text }] as UIMessage["parts"]);
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: "user", parts: userParts },
      ]);

      const assistantMsgId = nextMsgId();
      const streamAccumulator = createNormalizedChatMessageAccumulator({
        messageId: assistantMsgId,
        flushOnDone: "if-media",
        commitMessage: (message, isUpdate) => {
          setMessages((prev) =>
            isUpdate
              ? prev.map((m) => (m.id === message.id ? message : m))
              : [...prev, message],
          );
        },
        callbacks: {
          onImageGenerated: (url) => onImageGeneratedRef.current?.(url),
          onVideoGenerated: (url, thumbnailUrl) =>
            onVideoGeneratedRef.current?.(url, thumbnailUrl),
          onToolResult: (event) => onToolResultRef.current?.(event),
          onToolUse: (event) => onToolUseRef.current?.(event),
          onScoutPhase: (event) => onScoutPhaseRef.current?.(event),
          onImageProgress: (event) => onImageProgressRef.current?.(event),
        },
      });

      try {
        const token = await getAccessToken();
        const currentAgentName = agentNameRef.current;
        const resolvedUnifiedAgentName =
          runtimeTarget === "unified"
            ? currentAgentName ?? agentIdRef.current ?? null
            : null;
        const isVideoEditorAgent = resolvedUnifiedAgentName === VIDEO_EDITOR_AGENT_NAME;

        // ── Build request body ───────────────────────────────────────
        const body: Record<string, unknown> = {
          type: "user_message",
          userMessage: text,
        };
        if (currentTitle) {
          body.title = currentTitle;
        }
        if (sessionIdRef.current) {
          body.sessionId = sessionIdRef.current;
        }
        if (runtimeTarget === "unified") {
          // Unified loop expects ``agentName`` (DB-keyed) instead of
          // ``agentId`` (Anthropic Console agent_011...). Falls back to
          // agentId when agentName isn't supplied — useful during the
          // transition while callers still wire agentId only.
          if (resolvedUnifiedAgentName) {
            body.agentName = resolvedUnifiedAgentName;
          }
          const metadata = isVideoEditorAgent
            ? pickVideoEditorContext(contextRef.current)
            : contextRef.current;
          if (metadata) {
            body.metadata = metadata;
            if (isVideoEditorAgent) body.context = metadata;
          }
        } else if (agentIdRef.current) {
          body.agentId = agentIdRef.current;
        }

        // Scope sessions to the active workspace. Unified-runtime tools need
        // workspaceId on every turn; managed-agent sessions only need it when
        // creating the immutable Anthropic session metadata.
        const attachSessionScope = (target: Record<string, unknown>) => {
          const scopedWorkspaceId =
            workspaceIdRef.current ??
            currentScoutSeed?.workspaceId ??
            useAdsWorkspaceStore.getState().scopeWorkspaceId;
          if (scopedWorkspaceId) target.workspaceId = scopedWorkspaceId;
          if (currentScoutSeed) target.scoutSeed = currentScoutSeed;
        };
        if (runtimeTarget === "unified" || !sessionIdRef.current) attachSessionScope(body);

        // Ads workspace snapshot (same shape as Marcus). Agent Mads (the
        // meta_ads slot) is the creative-picker — it benefits from a broader
        // inventory than the default 10/10 cap, so we bump the per-type caps
        // for that agent only. Other managed agents (Banan, Vid, Higgsfield,
        // GAds) use the default cap to keep their per-turn context tight.
        if (!isVideoEditorAgent) {
          const isMads = agentIdRef.current === "meta_ads";
          const adsSnapshot = buildAdsContextSnapshot(
            isMads ? { imageAssetLimit: 25, videoAssetLimit: 25 } : undefined,
          );
          if (adsSnapshot) {
            body.adsContext = adsSnapshot;
          }
        }

        // User-picked widget contexts (same mechanism Marcus uses). Snapshot
        // and clear on send so they are scoped to this single turn.
        if (!isVideoEditorAgent) {
          const dept = useDepartmentStore.getState();
          const widgetContexts = dept.attachedWidgetContexts.filter(
            (c) => c.stepIndex >= dept.currentStepIndex,
          );
          if (widgetContexts.length > 0) {
            body.widgetContexts = widgetContexts;
            dept.clearWidgetContexts();
          }
        }

        // Image attachments: convert ChatAttachedImage → { data, mediaType }
        if (images && images.length > 0) {
          const imageInputs = images
            .filter((img) => img.type === "image" && img.dataUrl)
            .map((img) => ({
              data: stripDataUrlPrefix(img.dataUrl!),
              mediaType: img.mediaType,
            }));
          if (imageInputs.length > 0) {
            body.images = imageInputs;
          }
        }

        // ── POST and stream SSE ──────────────────────────────────────
        // Phase 8-9: route by runtime_target. SSE response shape is
        // NormalizedEvent-compatible on both endpoints.
        const postEndpoint =
          runtimeTarget === "unified"
            ? "/core/agent/unified-chat"
            : "/core/agent/managed-chat";
        const postChat = (payload: Record<string, unknown>) =>
          fetch(resolveApiUrl(postEndpoint), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });

        let res = await postChat(body);

        if (!res.ok) {
          const errorBody = await readChatErrorBody(res);
          if (
            runtimeTarget === "unified" &&
            res.status === 409 &&
            isSessionTooLongError(errorBody) &&
            typeof body.sessionId === "string"
          ) {
            sessionIdRef.current = null;
            setSessionId(null);
            delete body.sessionId;
            attachSessionScope(body);
            res = await postChat(body);
          }
          if (!res.ok) {
            const retryErrorBody = await readChatErrorBody(res);
            throw new Error(
              retryErrorBody.message ??
                retryErrorBody.error ??
                errorBody.message ??
                errorBody.error ??
                `Request failed: ${res.status} ${res.statusText}`,
            );
          }
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        const parser = createNormalizedChatStreamParser();

        while (true) {
          if (controller.signal.aborted) break;
          const { done, value } = await reader.read();
          if (done) break;

          for (const event of parser.pushChunk(decoder.decode(value, { stream: true }))) {
            const result = streamAccumulator.handleEvent(event);
            if (result.sessionId) {
              sessionIdRef.current = result.sessionId;
              setSessionId(result.sessionId);
            }
            if (result.startedStreaming) {
              setStatus("streaming");
            }
            if (result.error) {
              setError(result.error);
            }
            if (result.sessionTerminated) {
              setError(new Error("Session terminated by server"));
              sessionIdRef.current = null;
              setSessionId(null);
            }
          }
        }
      } catch (err) {
        const isAbortError =
          err instanceof DOMException
            ? err.name === "AbortError"
            : err instanceof Error && err.name === "AbortError";
        if (!stopRequestedRef.current && !isAbortError) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (activeControllerRef.current === controller) {
          activeControllerRef.current = null;
        }
        setStatus("ready");
        busyRef.current = false;
        stopRequestedRef.current = false;

        const nextTurn = queuedTurnsRef.current.shift();
        if (nextTurn) {
          setQueuedMessageCount(queuedTurnsRef.current.length);
          window.setTimeout(() => {
            void sendMessage(nextTurn.text, nextTurn.images, nextTurn.display);
          }, 0);
        }
      }
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const stopMessage = useCallback(() => {
    if (!busyRef.current && !activeControllerRef.current) return;
    stopRequestedRef.current = true;
    activeControllerRef.current?.abort();
    setStatus("ready");

    const sid = sessionIdRef.current;
    if (runtimeTarget !== "unified" || !sid) return;
    void (async () => {
      try {
        const token = await getAccessToken();
        await fetch(resolveApiUrl(`/core/agent/runtime-sessions/${sid}/interrupt`), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            kind: "hard",
            reason: "user_request",
          }),
        });
      } catch {
        // The local fetch abort above still releases the UI; server-side
        // interrupt is best-effort for cross-pod unified-runtime work.
      }
    })();
  }, [runtimeTarget]);

  return {
    messages,
    setMessages,
    status,
    error,
    sessionId,
    queuedMessageCount,
    sendMessage,
    stopMessage,
  };
}
